/**
 * ================================================================================
 *  KRIMA Combo Selector — assets/krima-combo-v2.js
 *  Versión: 6.3.1
 * ================================================================================
 *
 *  Los sobrecostos se definen en el metaobject mediante dos campos:
 *    - surcharge_products: qué productos tienen sobrecosto
 *    - surcharge_amount:   cuánto cuesta el sobrecosto (en COP, ej: 4000)
 *
 *  El precio real del producto no se toca — se vende igual de forma individual.
 *  El JS consulta surchargeProductIds para saber si un producto tiene cargo extra.
 *
 *  NUEVO v6.3.0 — Productos fijos:
 *  Un combo puede traer productos que NO son elegibles por el cliente (definidos
 *  en combo_config.fixed_products). Se leen desde #krima-combo-fixed-data y se
 *  agregan siempre a las properties del pedido, continuando la numeración de
 *  "Selección N" después de los slots seleccionables.
 *
 *  FIX v6.3.1 — Vínculo fee item ↔ combo:
 *  El fee item que se agrega por sobrecosto ahora se etiqueta con la property
 *  "_combo_parent_key" = key del line item del combo que lo originó. Antes no
 *  existía ningún vínculo, y krima-combo-cart-guard.js solo podía razonar "¿queda
 *  algún combo en el carrito?" — con 2+ combos en el carrito, eliminar el que
 *  tenía sobrecosto dejaba su fee item huérfano sin limpiar, porque el OTRO combo
 *  seguía presente. Ver krima-combo-cart-guard.js para el lado que consume este
 *  vínculo.
 * ================================================================================
 */

(function () {
  'use strict';

  const KrimaCombo = {
    container:      null,
    slotsContainer: null,
    config:         {},
    products:       [],
    fixedProducts:  [],
    surcharge:      { productIds: [], amountCents: 0 },
    feeProduct:     null,
    selections:     {},

    // ─── Init ───────────────────────────────────────────────────────────────────

    init() {
      this.container = document.getElementById('krima-combo');
      if (!this.container) return;

      this.slotsContainer = document.getElementById('krima-combo-slots');
      this._readConfig();
      this._readProducts();
      this._readFixedProducts();
      this._readSurcharge();
      this._readFeeProduct();
      this._fetchFeeProductIfNeeded();
      this._renderSlots();
      this._hookButton();
    },

    // ─── Lectura de datos ────────────────────────────────────────────────────────

    _readConfig() {
      const d = this.container.dataset;
      this.config = {
        slotCount:        parseInt(d.slotCount) || 0,
        slotLabel:        d.slotLabel           || 'Base',
        currency:         d.currency            || 'COP',
        feeProductHandle: d.feeProductHandle    || '',
      };
    },

    _readProducts() {
      const el = document.getElementById('krima-combo-products-data');
      if (!el) {
        // Combo 100% de productos fijos: no hay slots seleccionables, es esperado
        // que no exista este script tag.
        if (this.config.slotCount > 0) {
          console.warn('[KrimaCombo] Falta #krima-combo-products-data');
        }
        return;
      }
      try {
        const data = JSON.parse(el.textContent);

        // Nueva estructura: { common: [ids], products: [...], slots: { "2": [ids], ... } }
        // Compatibilidad con estructura anterior (array plano)
        if (Array.isArray(data)) {
          this.products     = data;
          this.slotProducts = {};
          this.commonIds    = new Set(data.map(p => p.id));
        } else {
          // Índice de productos por ID para lookup rápido
          this._productIndex = {};
          data.products.forEach(p => { this._productIndex[p.id] = p; });

          // IDs de la lista común
          this.commonIds = new Set(data.common ? String(data.common).split(',').map(Number).filter(Boolean) : data.products.map(p => p.id));

          // Lista completa de productos (para uso de surcharge, etc.)
          this.products = data.products;

          // Overrides por slot: { "2": [id, id], "3": [id] }
          this.slotProducts = data.slots || {};

          // Labels por slot: { "1": "Base", "3": "Crunchy Spread" }
          this.slotLabels = data.slotLabels || {};
        }

        console.log('[KrimaCombo] Productos cargados:', this.products.length,
          '| Slots con lista propia:', Object.keys(this.slotProducts));
      } catch (e) {
        console.error('[KrimaCombo] Error parseando productos:', e);
      }
    },

    // Devuelve el array de productos para un slot específico
    _getProductsForSlot(index) {
      const slotIds = this.slotProducts[String(index)];
      if (!slotIds || !slotIds.length) {
        // Sin override: usar lista común
        return this.products.filter(p => this.commonIds.has(p.id));
      }
      // Con override: usar lista específica del slot (en el orden definido)
      return slotIds
        .map(id => this._productIndex?.[id] || this.products.find(p => p.id === id))
        .filter(Boolean);
    },

    _readSurcharge() {
      const el = document.getElementById('krima-combo-surcharge-data');
      if (!el) return;
      try {
        const data = JSON.parse(el.textContent);
        this.surcharge = {
          productIds:  data.surchargeProductIds  || [],
          amountCents: data.surchargeAmountCents || 0,
        };
        console.log('[KrimaCombo] Sobrecosto configurado:', this.surcharge);
      } catch (e) {
        console.warn('[KrimaCombo] Error parseando configuración de sobrecosto:', e);
      }
    },

    // Productos fijos: siempre van en el combo, sin selección del cliente
    _readFixedProducts() {
      const el = document.getElementById('krima-combo-fixed-data');
      if (!el) return;
      try {
        this.fixedProducts = JSON.parse(el.textContent) || [];
        console.log('[KrimaCombo] Productos fijos cargados:', this.fixedProducts.length);
      } catch (e) {
        console.error('[KrimaCombo] Error parseando productos fijos:', e);
      }
    },

    _readFeeProduct() {
      const el = document.getElementById('krima-fee-product-data');
      if (!el) return;
      try {
        const data = JSON.parse(el.textContent);
        // Validar que el JSON tiene datos reales (no un producto vacío)
        if (data && data.id && data.variants?.length) {
          this.feeProduct = data;
          console.log('[KrimaCombo] Fee product cargado desde Liquid:', this.feeProduct.id);
        }
      } catch (e) {
        console.warn('[KrimaCombo] Error parseando fee product desde Liquid:', e);
      }
    },

    // Fallback: si el Liquid no resolvió el fee product, fetchearlo por handle
    _fetchFeeProductIfNeeded() {
      if (this.feeProduct || !this.config.feeProductHandle) return;

      const handle = this.config.feeProductHandle;
      console.log('[KrimaCombo] Fetching fee product por handle:', handle);

      fetch(`/products/${handle}.js`)
        .then(r => {
          if (!r.ok) throw new Error(`Fee product "${handle}" no encontrado (${r.status})`);
          return r.json();
        })
        .then(product => {
          this.feeProduct = {
            id:       product.id,
            handle:   product.handle,
            title:    product.title,
            // La API del storefront devuelve precios como enteros en centavos directamente
            // en .js endpoint (a diferencia de .json que devuelve strings)
            variants: product.variants.map(v => ({
              id:    v.id,
              price: v.price,   // ya en centavos (ej: 5000000 para $50.000 COP)
              title: v.title,
              sku:   v.sku,
            })),
          };
          console.log('[KrimaCombo] Fee product cargado via fetch:', this.feeProduct.title, '— variantes:', this.feeProduct.variants.map(v => `${v.title}: ${v.price}`));
        })
        .catch(err => {
          console.warn('[KrimaCombo] No se pudo cargar el fee product:', err.message);
        });
    },

    // ─── Helpers ─────────────────────────────────────────────────────────────────

    _hasSurcharge(productId) {
      return this.surcharge.productIds.includes(productId);
    },

    _formatMoney(cents) {
      const amount = Math.round(cents / 100);
      if (this.config.currency === 'COP') {
        return `$${amount.toLocaleString('es-CO')}COP`;
      }
      return `$${(cents / 100).toFixed(2)}`;
    },

    _buildDisplayName(product, variant) {
      if (variant.title === 'Default Title' || product.variants.length === 1) return product.title;
      return `${product.title} - ${variant.title}`;
    },

    // ─── Renderizado de slots ────────────────────────────────────────────────────

    _renderSlots() {
      if (!this.slotsContainer || !this.config.slotCount || !this.products.length) return;
      this.slotsContainer.innerHTML = '';
      for (let i = 1; i <= this.config.slotCount; i++) {
        this.slotsContainer.appendChild(this._createSlot(i));
      }
    },

    _createSlot(index) {
      const wrapper = document.createElement('div');
      wrapper.className = 'krima-combo__slot';
      wrapper.dataset.slot = index;

      const label = document.createElement('label');
      label.className = 'krima-combo__slot-label';
      label.htmlFor = `krima-slot-${index}`;
      const slotLabelText = this.slotLabels[String(index)] || this.config.slotLabel;
      label.innerHTML = `${slotLabelText} <span class="krima-combo__required" aria-hidden="true">*</span>`;

      const subLabel = document.createElement('p');
      subLabel.className = 'krima-combo__slot-sublabel';
      subLabel.id = `krima-slot-sublabel-${index}`;
      subLabel.setAttribute('aria-live', 'polite');

      const selectWrapper = document.createElement('div');
      selectWrapper.className = 'krima-combo__select-wrapper';

      const select = document.createElement('select');
      select.id = `krima-slot-${index}`;
      select.className = 'krima-combo__select';
      select.required = true;
      select.dataset.slot = index;
      select.setAttribute('aria-required', 'true');
      select.setAttribute('aria-describedby', `krima-slot-sublabel-${index}`);

      const defaultOpt = document.createElement('option');
      defaultOpt.value = '';
      defaultOpt.textContent = 'Selecciona';
      defaultOpt.selected = true;
      defaultOpt.disabled = true;
      select.appendChild(defaultOpt);

      // Usar lista específica del slot si existe, o la lista común
      const slotProductList = this._getProductsForSlot(index);

      slotProductList.forEach(product => {
        product.variants.forEach(variant => {
          if (!variant.available) return;

          const displayName   = this._buildDisplayName(product, variant);
          const hasSurcharge  = this._hasSurcharge(product.id);
          const surchargeText = hasSurcharge
            ? ` (+ ${this._formatMoney(this.surcharge.amountCents)})`
            : '';

          const opt = document.createElement('option');
          opt.value = JSON.stringify({
            productId:  product.id,
            variantId:  variant.id,
            sku:        variant.sku,
            barcode:    variant.barcode,
            title:      displayName,
            hasSurcharge,
          });
          opt.textContent = `${displayName}${surchargeText}`;
          select.appendChild(opt);
        });
      });

      select.addEventListener('change', (e) => this._onSlotChange(e, index, subLabel));

      selectWrapper.appendChild(select);
      wrapper.appendChild(label);
      wrapper.appendChild(subLabel);
      wrapper.appendChild(selectWrapper);
      return wrapper;
    },

    // ─── Cambio de selección ─────────────────────────────────────────────────────

    _onSlotChange(event, index, subLabel) {
      const value = event.target.value;
      const slotEl = this.slotsContainer.querySelector(`[data-slot="${index}"]`);
      if (slotEl) slotEl.classList.remove('krima-combo__slot--error');

      if (!value) { this.selections[index] = null; subLabel.textContent = ''; return; }

      try {
        const data = JSON.parse(value);
        this.selections[index] = data;
        subLabel.textContent = data.hasSurcharge
          ? `${data.title} (+ ${this._formatMoney(this.surcharge.amountCents)})`
          : data.title;
      } catch (e) {
        console.error('[KrimaCombo] Error parseando selección:', e);
      }
    },

    // ─── Propiedades y sobrecosto ─────────────────────────────────────────────────

    // Formato en el pedido:
    //   Selección 1: Base de Almendras 90g | EAN: 11111111 | SKU: ALMD-90
    //
    // Los slots seleccionables ocupan "Selección 1".."Selección slotCount".
    // Los productos fijos (no elegibles) continúan la numeración justo después.
    _buildProperties() {
      const properties = {};

      Object.entries(this.selections).forEach(([idx, sel]) => {
        if (!sel) return;
        const sku = sel.sku?.trim() ? sel.sku : 'Sin SKU';
        const ean = sel.barcode?.trim() ? sel.barcode : 'Sin EAN';
        properties[`Selección ${idx}`] = `${sel.title} | SKU: ${sku} | EAN: ${ean}`;
      });

      this.fixedProducts.forEach((fp, i) => {
        const idx = this.config.slotCount + i + 1;
        const sku = fp.sku?.trim() ? fp.sku : 'Sin SKU';
        const ean = fp.barcode?.trim() ? fp.barcode : 'Sin EAN';
        properties[`Selección ${idx}`] = `${fp.title} | SKU: ${sku} | EAN: ${ean}`;
      });

      return properties;
    },

    _calcTotalSurcharge() {
      const slotsWithSurcharge = Object.values(this.selections)
        .filter(sel => sel?.hasSurcharge).length;
      return slotsWithSurcharge * this.surcharge.amountCents;
    },

    _findFeeVariant(totalCents) {
      if (!this.feeProduct?.variants?.length) return null;
      return this.feeProduct.variants.find(v => v.price === totalCents)
          || this.feeProduct.variants[0];
    },

    // Agrega el fee product al carrito, vinculado al line item del combo que lo
    // originó vía "_combo_parent_key". krima-combo-cart-guard.js usa ese vínculo
    // para saber a qué combo pertenece cada fee item y así limpiar huérfanos
    // correctamente cuando hay varios combos en el carrito al mismo tiempo.
    _addFeeProduct(feeVariant, parentKey) {
      const properties = { '_combo_fee': 'true' };
      if (parentKey) properties['_combo_parent_key'] = parentKey;

      Object.entries(this.selections)
        .filter(([, sel]) => sel?.hasSurcharge)
        .forEach(([idx, sel]) => { properties[`_combo_fee_sku_${idx}`] = sel.sku || 'Sin SKU'; });

      return fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items: [{ id: feeVariant.id, quantity: 1, properties }] }),
      }).catch(err => console.warn('[KrimaCombo] Error agregando fee product:', err));
    },

    // ─── Intercepción del botón ───────────────────────────────────────────────────

    _hookButton() {
      const btn = [
        '.add-to-cart-btn',
        'button[name="add"]',
        '[data-add-to-cart]',
        '[data-product-form] button[type="submit"]',
        '.product-form__submit',
        'form button[type="submit"]',
      ].map(s => document.querySelector(s)).find(Boolean);

      if (!btn) {
        console.warn('[KrimaCombo] No se encontró el botón de agregar al carrito.');
        return;
      }

      console.log('[KrimaCombo] Botón encontrado:', btn);

      btn.addEventListener('click', (e) => {
        if (!this._validateSelections()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        this._patchFetch(this._buildProperties(), this._calcTotalSurcharge());
      }, { capture: true });
    },

    // ─── Parche de window.fetch ───────────────────────────────────────────────────

    _patchFetch(properties, totalSurcharge) {
      const originalFetch = window.fetch;
      const self = this;

      window.fetch = function (url, options = {}) {
        const urlStr = typeof url === 'string' ? url : url?.url || '';

        if (urlStr.includes('/cart/add')) {
          window.fetch = originalFetch;

          try {
            const rawBody = options.body;

            if (rawBody instanceof FormData) {
              // ── FormData (Alpine.js) ──
              Object.entries(properties).forEach(([key, val]) => {
                rawBody.set(`properties[${key}]`, val);
              });

            } else if (typeof rawBody === 'string' && rawBody.trim().startsWith('{')) {
              // ── JSON body ──
              const body = JSON.parse(rawBody);
              if (Array.isArray(body.items)) {
                body.items = body.items.map(item => ({
                  ...item,
                  properties: { ...properties, ...(item.properties || {}) },
                }));
              } else if (body.id) {
                body.properties = { ...properties, ...(body.properties || {}) };
              }
              options = { ...options, body: JSON.stringify(body) };

            } else if (typeof rawBody === 'string') {
              // ── URLSearchParams ──
              const params = new URLSearchParams(rawBody);
              Object.entries(properties).forEach(([key, val]) => {
                params.set(`properties[${key}]`, val);
              });
              options = { ...options, body: params.toString() };
            }

            console.log('[KrimaCombo] Propiedades inyectadas:', properties);
          } catch (e) {
            console.error('[KrimaCombo] Error inyectando propiedades:', e);
          }

          // Agregar fee product si hay sobrecosto
          if (totalSurcharge > 0) {
            const feeVariant = self._findFeeVariant(totalSurcharge);
            if (feeVariant) {
              console.log(`[KrimaCombo] Agregando sobrecosto: ${self._formatMoney(totalSurcharge)} → variante ${feeVariant.id}`);
              const mainResult = originalFetch.call(this, url, options);
              mainResult.then(res => {
                if (!res.ok) return;

                // Leemos el key del combo recién agregado desde una copia de la
                // respuesta (clone) para no interferir con quien llamó a fetch
                // originalmente y todavía necesita leer el body.
                res.clone().json()
                  .then(data => Array.isArray(data.items) ? data.items[0]?.key : data.key)
                  .catch(() => null)
                  .then(parentKey => {
                    if (!parentKey) {
                      console.warn('[KrimaCombo] No se pudo determinar el key del combo; el fee product quedará sin vínculo directo.');
                    }
                    self._addFeeProduct(feeVariant, parentKey);
                  });
              });
              return mainResult;
            } else {
              console.warn('[KrimaCombo] Fee product no configurado. El sobrecosto no se cobrará.');
            }
          }
        }

        return originalFetch.call(this, url, options);
      };

      setTimeout(() => {
        if (window.fetch !== originalFetch) {
          window.fetch = originalFetch;
          console.warn('[KrimaCombo] Parche de fetch restaurado por timeout.');
        }
      }, 3000);
    },

    // ─── Validación ──────────────────────────────────────────────────────────────

    _validateSelections() {
      let valid = true;
      let firstErrorSlot = null;

      for (let i = 1; i <= this.config.slotCount; i++) {
        const slotEl = this.slotsContainer?.querySelector(`[data-slot="${i}"]`);
        if (!this.selections[i]) {
          valid = false;
          if (slotEl) slotEl.classList.add('krima-combo__slot--error');
          if (!firstErrorSlot) firstErrorSlot = slotEl;
        } else {
          if (slotEl) slotEl.classList.remove('krima-combo__slot--error');
        }
      }

      if (!valid) {
        this._showError(`Por favor selecciona una opción para cada ${this.config.slotLabel}.`);
        if (firstErrorSlot) {
          firstErrorSlot.querySelector('select')?.focus();
          firstErrorSlot.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        this._hideError();
      }

      return valid;
    },

    // ─── UI helpers ──────────────────────────────────────────────────────────────

    _showError(message) {
      const el = document.getElementById('krima-combo-error');
      if (!el) return;
      el.textContent   = message;
      el.style.display = 'block';
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },

    _hideError() {
      const el = document.getElementById('krima-combo-error');
      if (el) el.style.display = 'none';
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => KrimaCombo.init());
  } else {
    KrimaCombo.init();
  }

  window.KrimaCombo = KrimaCombo;

})();
